'use no memo';

import React from 'react';
import { Restaurant, useRestaurantQuery } from '@local/api';
import { mParticle } from '@local/analytics';
import { getTrafficSource } from '@local/utils';
import { isMarketplaceLive } from '@local/data-utils/restaurant';

import {
  getHostnameForMarketplace,
  getRestaurantIdFromUrl,
  isDirectUrl,
} from 'helpers/url';
import {
  getURLRequiresRedirect,
  setIsDirectToMpRedirect,
  shouldDirectToWebsiteChannel,
} from 'helpers/configureRedirects';
import { DEPLOY_ENVIRONMENTS, NO_DIRECT_LIVE_CHANNEL } from 'helpers/constants';
import { BrowserRouter as Router } from 'react-router';
import { PlatformProvider } from 'context/PlatformContext';
import { ThemeProvider } from 'context/ThemeContext';
import OrderPollingTimerProvider from 'context/OrderPollingTimerContext';
import PostCheckoutOrderProvider from 'context/PostCheckoutOrderContext';
import OrderProvider from 'context/OrderContext';
import { RestaurantProvider } from 'context/RestaurantContext';
import { UserProvider } from 'context/UserContext';
import { MenuProvider } from 'context/MenuContext';
import { ModalProvider, ModalRenderer } from 'context/ModalContext';
import ValidateOrderProvider from 'context/ValidateOrderProvider';
import App from 'App';
import { HelmetProvider } from 'react-helmet-async';
import { getPlatformName } from 'helpers/platform';
import { initializeYelp } from 'helpers/yelp';
import {
  ANALYTICS_EVENT_NAME,
  initSentry,
  logAnalyticsEvent,
  logMessage,
} from 'helpers/loggers';
import { useCookieConsentSync } from 'hooks/useCookieConsentSync';

import './styles/main.scss';

initSentry(process.env.VERSION);
initializeYelp();

export const ApplicationRoot: React.FunctionComponent = () => {
  const restaurantId = getRestaurantIdFromUrl();
  const { data, isLoading } = useRestaurantQuery(restaurantId ?? '');
  useCookieConsentSync();

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('is_mp_redirect')) {
      setIsDirectToMpRedirect();
    }
  }, []);

  React.useEffect(() => {
    if (!isLoading && data) {
      redirectInstagramToMarketplace(data);
      redirectToMarketplaceMenu(data);
      redirectToWebsiteChannel(data);
    }
  }, [data, isLoading]);

  const cnPlatform = getPlatformName();

  return (
    <HelmetProvider>
      <Router>
        <PlatformProvider platform={cnPlatform}>
          <OrderPollingTimerProvider>
            {/* @ts-expect-error PostCheckoutProvider needs to be refactored still */}
            <PostCheckoutOrderProvider>
              <OrderProvider>
                <RestaurantProvider>
                  <ThemeProvider>
                    <UserProvider>
                      <MenuProvider>
                        <ModalProvider>
                          <ValidateOrderProvider>
                            <ModalRenderer />
                            <App />
                          </ValidateOrderProvider>
                        </ModalProvider>
                      </MenuProvider>
                    </UserProvider>
                  </ThemeProvider>
                </RestaurantProvider>
              </OrderProvider>
            </PostCheckoutOrderProvider>
          </OrderPollingTimerProvider>
        </PlatformProvider>
      </Router>
    </HelmetProvider>
  );
};

/**
 * redirect instagram and facebook to marketplace
 * @param restaurant
 */
const redirectInstagramToMarketplace = (
  restaurant: Pick<Restaurant, 'is_discover_live'>
) => {
  if (
    getURLRequiresRedirect(window.location) &&
    isMarketplaceLive(restaurant)
  ) {
    const domain = getHostnameForMarketplace();
    const path = window.location.pathname;

    logMessage(`Redirecting OD Instagram URL to Marketplace ${domain}`);
    window.location.href = `https://${domain}${path}${window.location.search}`;
  }
};

const redirectToMarketplaceMenu = (
  restaurant: Pick<
    Restaurant,
    'id' | 'is_direct_live' | 'is_ordering_network_live'
  >
): void => {
  // Check if we're embedded in an iframe (any parent domain, not just Yelp)
  const isEmbedded = window.top !== window;
  if (!isEmbedded && shouldRedirectToMarketplace(restaurant)) {
    setIsDirectToMpRedirect();

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('is_mp_redirect', 'true');

    logAnalyticsEvent({
      eventName: ANALYTICS_EVENT_NAME.redirectToMarketplaceMenu,
      attributes: {
        restaurant_location_id: restaurant.id,
        source: 'direct-url',
      },
    });
    // flushes the event queue and forces the event to send to mParticle
    mParticle.upload();
    const domain = getHostnameForMarketplace();
    const path = window.location.pathname;

    window.location.href = `https://${domain}${path}?${searchParams.toString()}`;
  }
};

// todo duplicate of configureRedirect function without restaurant call
const shouldRedirectToMarketplace = (
  restaurant: Pick<
    Restaurant,
    'id' | 'is_direct_live' | 'is_ordering_network_live'
  >
) => {
  // avoid redirecting on local. Because local dev hostnames are shared between OD and Marketplace, this would cause an infinite loop.
  const deployEnv = process.env.REACT_APP_DEPLOY_ENV;
  if (deployEnv === DEPLOY_ENVIRONMENTS.local) return false;

  if (!isDirectUrl()) return false;

  // don't redirect if coming from OBN
  if (getTrafficSource(restaurant.id)) return false;

  return !restaurant.is_direct_live && !restaurant.is_ordering_network_live;
};

const redirectToWebsiteChannel = (
  restaurant: Pick<Restaurant, 'is_direct_live' | 'is_ordering_network_live'>
): void => {
  const path = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  const trafficSourceParam = searchParams.get('cn_channel');
  if (shouldDirectToWebsiteChannel(restaurant) && !trafficSourceParam) {
    // flushes the event queue and forces the event to send to mParticle
    mParticle.upload();
    searchParams.set('cn_channel', NO_DIRECT_LIVE_CHANNEL);
    window.location.href = `${window.location.origin}${path}?${searchParams.toString()}`;
  }
};
